import { z } from 'zod';

/**
 * The staff grant path. Seeded by `supabase/migrations/20260920000009_staff.sql` into
 * `staff_permissions` / `staff_role_permissions`, apart from the customer tables; `permissions.test.ts` asserts
 * the two agree. Every one of these needs a session at `aal2` in SQL: this map only decides what /admin shows.
 */
export const STAFF_PERMISSIONS = [
  'staff.orgs.view',
  'staff.orgs.manage',
  'staff.users.view',
  'staff.audit.view',
  'staff.projects.view',
  'staff.projects.edit',
  'staff.roles.manage',
  // first used in Stages 9-10
  'staff.pricing.manage',
  'staff.account.manage',
  'staff.ledger.adjust',
  'staff.orders.view',
  'staff.ledger.view',
  'staff.ledger.record',
  // cancel an order, mark it refunded
  'staff.orders.manage',
] as const;
export const staffPermissionSchema = z.enum(STAFF_PERMISSIONS);
export type StaffPermission = z.infer<typeof staffPermissionSchema>;

export const STAFF_ROLES = ['staff_admin', 'finance', 'fulfiller', 'viewer'] as const;
export const staffRoleSchema = z.enum(STAFF_ROLES);
export type StaffRole = z.infer<typeof staffRoleSchema>;

export const STAFF_ROLE_PERMISSIONS: Readonly<Record<StaffRole, readonly StaffPermission[]>> = {
  staff_admin: STAFF_PERMISSIONS,
  finance: [
    'staff.orgs.view',
    'staff.users.view',
    'staff.audit.view',
    'staff.orders.view',
    'staff.ledger.view',
    'staff.ledger.record',
    'staff.orders.manage',
  ],
  fulfiller: ['staff.orgs.view', 'staff.projects.view', 'staff.projects.edit'],
  viewer: ['staff.orgs.view', 'staff.projects.view'],
};

/** What /admin may show for someone holding these staff roles. The database stays the authority. */
export function staffPermissionSet(roles: readonly StaffRole[]): ReadonlySet<StaffPermission> {
  return new Set(roles.flatMap((role) => STAFF_ROLE_PERMISSIONS[role]));
}

/** A row of `public.staff_roles`. `scope_organization_id` is always null until assignment scoping exists (Q8). */
export const staffRoleRowSchema = z.object({
  user_id: z.uuid(),
  scope: z.literal('staff'),
  role: staffRoleSchema,
  scope_organization_id: z.null(),
  granted_by: z.uuid().nullable(),
  created_at: z.iso.datetime({ offset: true }),
});
export type StaffRoleRow = z.infer<typeof staffRoleRowSchema>;

/** Assurance level of a session: staff permissions need `aal2` (a verified TOTP challenge). */
export const aalSchema = z.enum(['aal1', 'aal2']);
export type Aal = z.infer<typeof aalSchema>;

const timestamp = z.iso.datetime({ offset: true });

/** A row of `staff_list_users`. */
export const adminUserSchema = z.object({
  user_id: z.uuid(),
  email: z.string(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  created_at: timestamp,
  confirmed: z.boolean(),
  membership_count: z.number().int(),
  is_staff: z.boolean(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;

/** A row of `staff_list_user_memberships`. */
export const adminUserMembershipSchema = z.object({
  organization_id: z.uuid(),
  organization_name: z.string(),
  organization_slug: z.string(),
  role: z.string(),
  deleted: z.boolean(),
  joined_at: timestamp,
});
export type AdminUserMembership = z.infer<typeof adminUserMembershipSchema>;

/** A row of `staff_list_staff`: one person with all their staff roles. */
export const staffMemberSchema = z.object({
  user_id: z.uuid(),
  email: z.string(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  roles: z.array(staffRoleSchema),
  since: timestamp,
});
export type StaffMember = z.infer<typeof staffMemberSchema>;

/** Input of `staff_create_organization` / `staff_update_organization`: staff choose the currency (Q15, Q41). */
export const staffOrganizationInputSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(80, 'Max 80 characters'),
  country: z.string().regex(/^[A-Z]{2}$/),
  currency: z.enum(['USD', 'CAD']),
});
export type StaffOrganizationInput = z.infer<typeof staffOrganizationInputSchema>;
